import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { flexRender, type Column, type Row, type Table } from '@tanstack/react-table';
import { MOBILE_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { cn } from '../../lib/utils';

/** The slice of a column's `meta` this shell reads. */
interface CellMeta {
  align?: 'right';
  padding?: 'tight';
}

interface DataTableProps<TData> {
  table: Table<TData>;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  /** Detail panel rendered in a full-width row under an expanded row. */
  renderExpanded?: (row: Row<TData>) => ReactNode;
  className?: string;
}

/**
 * The one table shell for every list view (BOT-392).
 *
 * Width strategy, per the Confluence review: keep every column and scroll
 * horizontally rather than dropping data, but pin the identifying columns
 * (checkbox, expand, SKU) to the left and Actions to the right so a row is
 * never anonymous and never un-actionable mid-scroll.
 *
 * Pinning is dropped entirely below the mobile breakpoint — Greenlight on a
 * phone is occasional read-only use, where four pinned columns would eat most
 * of the viewport and plain scrolling reads better.
 *
 * Headers are deliberately inert — no sort affordance is rendered anywhere,
 * because pagination is server-side and client-side sorting would silently
 * reorder one page while looking like it sorted everything.
 * decision: tablesort — see decisions/tablesort.md
 */
export function DataTable<TData>({
  table,
  loading,
  error,
  emptyMessage = 'Nothing to show',
  renderExpanded,
  className,
}: DataTableProps<TData>) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const pinningOn = !isMobile;

  // The table is as wide as its widest row, so a full-width detail row would be
  // that wide too — pushing most of the panel past the right edge and only
  // reachable by scrolling the whole table. Measuring the scroll viewport lets
  // the panel pin to the visible area instead (see the sticky wrapper below).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const visibleLeafCount = table.getVisibleLeafColumns().length;
  const rows = table.getRowModel().rows;

  /** Sticky offset + seam border for a pinned column. */
  function pinStyle(column: Column<TData, unknown>): CSSProperties {
    if (!pinningOn) return {};
    const pinned = column.getIsPinned();
    if (!pinned) return {};
    return pinned === 'left'
      ? { position: 'sticky', left: column.getStart('left') }
      : { position: 'sticky', right: column.getAfter('right') };
  }

  function pinClasses(column: Column<TData, unknown>, kind: 'head' | 'cell'): string {
    if (!pinningOn) return '';
    const pinned = column.getIsPinned();
    if (!pinned) return '';
    const isLastLeft = pinned === 'left' && column.getIsLastColumn('left');
    const isFirstRight = pinned === 'right' && column.getIsFirstColumn('right');
    return cn(
      kind === 'head' ? 'z-30' : 'z-10',
      // The seam marks where the frozen region ends, so a scrolled table still
      // reads as "identity | data | actions".
      isLastLeft && 'border-r border-secondary',
      isFirstRight && 'border-l border-secondary',
    );
  }

  const stateMessage = error ?? (loading ? 'Loading…' : rows.length === 0 ? emptyMessage : null);

  /** Horizontal padding for a cell. The control columns (expand, actions) opt
   *  into `tight` so they stop reserving text-column padding around a 24-28px
   *  button — and so their rendered width matches the `size` the pinned
   *  offsets are computed from. */
  function padClass(column: Column<TData, unknown>): string {
    const meta = column.columnDef.meta as CellMeta | undefined;
    return meta?.padding === 'tight' ? 'px-1.5' : 'px-3.5';
  }

  return (
    <div
      ref={scrollRef}
      className={cn(
        'min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-background',
        className,
      )}
    >
      <table className="w-max min-w-full border-separate border-spacing-0 tabular-nums">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const meta = header.column.columnDef.meta as CellMeta | undefined;
                // Only pinned columns get an explicit width: their sticky offsets
                // are computed from `size`, so the rendered width has to match it.
                // Unpinned columns size to content — applying a width there would
                // stamp TanStack's 150px default onto every column and inflate the
                // table well past the viewport for no reason.
                const fixedWidth =
                  pinningOn && header.column.getIsPinned() ? header.getSize() : undefined;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    style={{ ...pinStyle(header.column), width: fixedWidth }}
                    className={cn(
                      'sticky top-0 z-20 h-10 whitespace-nowrap border-b border-border bg-zinc-50',
                      padClass(header.column),
                      'text-xs font-medium text-muted-foreground',
                      meta?.align === 'right' ? 'text-right' : 'text-left',
                      pinClasses(header.column, 'head'),
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {stateMessage ? (
            <tr>
              <td
                colSpan={visibleLeafCount}
                className={cn(
                  'px-4 py-8 text-center text-[13px]',
                  error ? 'text-red-600' : 'text-muted-foreground',
                )}
              >
                {stateMessage}
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const expanded = row.getIsExpanded();
              return (
                <Fragment key={row.id}>
                  <tr
                    // --sbg feeds the pinned cells' background so they track the
                    // row's own state; without it a sticky cell would show the
                    // table background and the columns beneath would scroll through.
                    style={{ ['--sbg' as string]: expanded ? '#fafafa' : '#ffffff' }}
                    className="[background:var(--sbg)] hover:[--sbg:#f7f7f8]"
                  >
                    {row.getVisibleCells().map(cell => {
                      const meta = cell.column.columnDef.meta as CellMeta | undefined;
                      return (
                        <td
                          key={cell.id}
                          style={pinStyle(cell.column)}
                          className={cn(
                            'border-b border-secondary py-2 [background:var(--sbg)]',
                            padClass(cell.column),
                            meta?.align === 'right' ? 'text-right' : 'text-left',
                            pinClasses(cell.column, 'cell'),
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>

                  {expanded && renderExpanded && (
                    <tr>
                      <td colSpan={visibleLeafCount} className="border-b border-border bg-zinc-50 p-0">
                        <div
                          className="sticky left-0"
                          style={viewportWidth ? { width: viewportWidth } : undefined}
                        >
                          {renderExpanded(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
